import React, {useState, useCallback, useEffect, useMemo} from 'react';
import t from 'babel-plugin-yate/macro';
import {useDispatch} from 'react-redux';
import debounce from 'lodash-es/debounce';
import {Field, Form} from 'react-final-form';
import Button from '../../components/Button';
import {
  formatRON,
  formatPercent,
  parseNumberIntegerFormField,
} from '../../lib/misc';
import {Select} from '../../components/Form/Select';

import Label from '../../components/Form/Label';
import {Input} from '../../components/Form/Input';
import {
  composeFormValidators,
  isRequired,
  isAlphaNumeric,
  hasMaxLength,
  isNumber,
  isInRange,
  isMultipleOf,
} from '../../lib/validations';
import Text from '../../components/uiElem/Text';
import {postEstimatedOffers} from '../../store/request';

const SLIDER_STEP = 100;

function Toggle({disabled, checked, onChange}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-8 w-16 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-purple-light' : 'bg-grey-1'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 mt-1 ${
          checked ? 'translate-x-8' : 'translate-x-2'
        }`}
      />
      <span
        className={`absolute top-0 bottom-0 flex items-center text-10 px-3 uppercase ${checked ? 'left-0 text-white' : 'right-0 text-black'}`}>
        {checked ? 'Da' : 'Nu'}
      </span>
    </button>
  );
}

function SliderInput({value, min, max, step, onChange}) {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div className="relative flex items-center h-6">
      <div className="absolute inset-x-0 h-1 bg-grey-1 rounded-full" />
      <div
        className="absolute h-1 bg-purple-light rounded-full pointer-events-none"
        style={{width: `${pct}%`, maxWidth: '100%'}}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="relative z-10 w-full h-1 appearance-none bg-transparent cursor-pointer"
      />
    </div>
  );
}

export default function OfferGroupRefinanceItem(props) {
  const {
    heading,
    item,
    onSelect,
    onChangeInsurance,
    hasOneItem,
    uuid,
    isCreditCard,
    isConsumerFinanceWithCard,
  } = props;
  const {
    id: offerId,
    insurance,
    paired_offer_responses,
    refinance_amount,
    is_revolving_eligible,
    revolving_amount,
  } = item;

  const dispatch = useDispatch();
  const hasInsurance = insurance === 1;
  const firstCredit = paired_offer_responses?.[0] || {};
  const [credit, setCredit] = useState(firstCredit);
  const MIN =
    credit.min_amount_slider - (credit.min_amount_slider % SLIDER_STEP);
  const MAX =
    credit.max_amount_slider - (credit.max_amount_slider % SLIDER_STEP);

  const [additionalAmount, setAdditionalAmount] = useState(MAX);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formInitialValues = useMemo(
    () => ({plusAmount: MAX, refinaceAmount: refinance_amount}),
    [MAX, refinance_amount],
  );

  const handleSelectInstallments = useCallback(
    months => {
      const newCredit =
        paired_offer_responses.find(cr => cr?.installments === months) ||
        firstCredit;
      setCredit(newCredit);
      setAdditionalAmount(newCredit?.max_amount_slider);
    },
    [paired_offer_responses, firstCredit],
  );

  const debouncedEstimatedOffers = useMemo(
    () =>
      debounce(async amount => {
        if (isNaN(amount)) return;

        const estimatedOffers = await dispatch(
          postEstimatedOffers(uuid, {
            offer_id: offerId,
            loan_amount: refinance_amount + amount,
            installments: credit.installments,
          }),
        );

        if (estimatedOffers?.offers?.requested) {
          const {pmt, amount, interest, dae} = estimatedOffers.offers.requested;
          setCredit({
            ...credit,
            pmt,
            amount,
            dae,
            interest,
          });
        }
      }, 500),
    [dispatch, uuid, offerId, refinance_amount, credit],
  );

  const handleSliderChange = useCallback(
    value => {
      setAdditionalAmount(value);
      debouncedEstimatedOffers(value);
    },
    [debouncedEstimatedOffers],
  );

  const handleSelectOffer = useCallback(() => {
    onSelect(credit.id, isConsumerFinanceWithCard, {
      loan_amount: credit.amount,
      installments: credit.installments,
    });
  }, [
    credit.id,
    credit.amount,
    credit.installments,
    onSelect,
    isConsumerFinanceWithCard,
  ]);

  useEffect(() => {
    handleSelectInstallments();
  }, [handleSelectInstallments]);

  useEffect(
    () => () => debouncedEstimatedOffers.cancel(),
    [debouncedEstimatedOffers],
  );

  return (
    <Form
      onSubmit={() => {}}
      initialValues={formInitialValues}
      render={({form}) => {
        return (
          <React.Fragment>
            <div className="p-4 px-2 border-b">
              <Text ui="mars">{heading}</Text>
            </div>
            <div className="flex-grow pt-8">
              <div className="md:flex md:flex-wrap">
                <div className="w-full md:w-1/2 px-2 mb-6">
                  <Label value={t`Asigurare de viata`} />
                  <div className="pt-2">
                    <Toggle
                      disabled={hasOneItem}
                      checked={hasInsurance}
                      onChange={onChangeInsurance}
                    />
                  </div>
                </div>
                <div className="w-full md:w-1/2 px-2 mb-6">
                  <Field
                    name="refinaceAmount"
                    label={t`Suma de refinantat`}
                    component={Input}
                    disabled={true}
                    validate={composeFormValidators(
                      isRequired(),
                      isAlphaNumeric(),
                      hasMaxLength(255),
                    )}
                  />
                </div>
              </div>

              <div className="md:flex md:flex-wrap">
                <div className="w-full px-2 mb-6">
                  <Field
                    name="plusAmount"
                    parse={value => {
                      let rangeValue = parseNumberIntegerFormField(value);

                      if (value < MIN) {
                        rangeValue = MIN;
                      } else if (value > MAX) {
                        rangeValue = MAX;
                      }

                      if (rangeValue % SLIDER_STEP === 0) {
                        handleSliderChange(rangeValue);
                      }
                      return rangeValue;
                    }}
                    type="number"
                    step={SLIDER_STEP}
                    validate={composeFormValidators(
                      isRequired(),
                      isNumber(),
                      isInRange(MIN, MAX),
                      isMultipleOf(SLIDER_STEP),
                    )}
                    render={({input, meta}) => (
                      <Input
                        input={input}
                        meta={{...meta, touched: meta.touched || meta.dirty}}
                        label={t`Suma suplimentara de care ai nevoie`}
                        type="number"
                        step={SLIDER_STEP}
                      />
                    )}
                  />

                  <div className="pt-1">
                    <SliderInput
                      value={additionalAmount}
                      min={MIN}
                      max={MAX}
                      step={SLIDER_STEP}
                      onChange={value => {
                        handleSliderChange(value);
                        form.change('plusAmount', value);
                      }}
                    />
                    <div className="flex justify-between mb-3">
                      <span>{formatRON(MIN)}</span>
                      <span>{formatRON(MAX)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="md:flex md:flex-wrap">
                <div className="w-full px-2 mb-6">
                  <Field
                    name="installments"
                    value={credit.installments}
                    label={t`Alege perioada`}
                    component={Select}
                    parse={value => {
                      const newValue = parseNumberIntegerFormField(value);
                      handleSelectInstallments(newValue);

                      return newValue;
                    }}>
                    {paired_offer_responses?.map((cr, index) => (
                      <option key={index} value={cr?.installments}>
                        {t`${cr?.installments} luni`}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>

              <div className="w-full px-2 mt-3 mb-6">
                <Label value={t`Oferta propusa`} />
                <div className="flex justify-between border-t py-2">
                  <Text ui="mars">
                    <div className="pr-2">{t`Rata lunara`}</div>
                  </Text>
                  <Text ui="mars">{formatRON(credit?.pmt)}</Text>
                </div>

                <div className="flex justify-between border-t py-1">
                  <Text ui="uranus">
                    <div className="pr-2">
                      {!isCreditCard
                        ? t`Credit value`
                        : t`Limita card acordata`}
                    </div>
                  </Text>
                  <Text ui="uranus">{formatRON(credit?.amount)}</Text>
                </div>

                {!isCreditCard &&
                  is_revolving_eligible &&
                  !isNaN(revolving_amount) && (
                    <div className="flex justify-between border-t py-1">
                      <Text ui="uranus">
                        <div className="pr-2">{t`Limita credit`}</div>
                      </Text>
                      <Text ui="uranus">{formatRON(revolving_amount)}</Text>
                    </div>
                  )}

                <div className="flex justify-between border-t py-1">
                  <Text ui="uranus">
                    <div className="pr-2">{t`DAE`}</div>
                  </Text>
                  <Text ui="uranus">{formatPercent(credit?.dae)}</Text>
                </div>

                <div className="flex justify-between border-t py-1">
                  <Text ui="uranus">
                    <div className="pr-2">{t`Interest`}</div>
                  </Text>
                  <Text ui="uranus">{formatPercent(credit?.interest)}</Text>
                </div>
              </div>
            </div>

            <div className="w-full px-2 mt-8 mb-4">
              <Button
                boxShadow={false}
                color="purple"
                border={false}
                customProps={{type: 'button'}}
                title={t`Aleg oferta`}
                onClick={handleSelectOffer}
                fullWidth
              />
            </div>
          </React.Fragment>
        );
      }}
    />
  );
}
